<template>
  <a-card
    v-if="isFull"
    size="small"
    title="Конструктор ссылок">
    <a-row :gutter="[16, 16]">
      <a-col
        v-if="isFull"
        :md="6"
        :xs="24">
        <label>Брендинг:</label>
        <a-select
          v-model:value="selectedBranding"
          :style="{width: '100%'}"
          :filter-option="fuzzyMatchOption"
          show-search>
          <a-select-option
            v-for="branding in brandingList"
            :key="branding.id"
            :label="branding.company_name"
            :value="branding.id">
            {{ branding.company_name }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col
        :md="isFull ? 6 : 8"
        :xs="24">
        <label>Камера ID:</label>
        <a-select
          v-model:value="selectedRoomType"
          :style="{width: '100%'}">
          <a-select-option
            v-for="(option) in currentFloorRoomOptions"
            :key="option.cameraId"
            :value="option.cameraId">
            {{ `${roomTypes[option.roomType]} №${getCameraNumber(option)} (${option.floorNumber} этаж)` }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col
        :md="isFull ? 6 : 8"
        :xs="24">
        <label>Миниплан:</label>
        <a-select
          v-model:value="selectedMiniPlan"
          :style="{width: '100%'}">
          <a-select-option
            v-for="(option) in miniPlanOptions"
            :key="option"
            :value="option">
            {{ option }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col
        :md="isFull ? 6 : 8"
        :xs="24">
        <label>Начальный FOV:</label>
        <a-input
          v-model:value="primaryFov"
          type="number"
          min="0"
          max="180"
          :style="{width: '100%'}" />
      </a-col>
    </a-row>

    <a-row
      :gutter="[16, 16]"
      style="margin-top: 1rem">
      <a-col
        v-if="isFull && showFloorDropdown"
        :md="6"
        :xs="24">
        <label>Этаж:</label>
        <a-select
          v-model:value="selectedFloor"
          :style="{width: '100%'}">
          <a-select-option
            v-for="(value, key) in floorList"
            :key="key"
            :value="key">
            {{ key + 1 }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col
        :md="isFull ? 6 : 8"
        :xs="24">
        <label>Язык:</label>
        <a-select
          v-model:value="selectedLanguage"
          :style="{width: '100%'}">
          <a-select-option
            v-for="(value, key) in availableLanguages"
            :key="key"
            :value="key">
            {{ value }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col
        v-if="isFull"
        :md="isFull ? 6 : 8"
        :xs="24">
        <label>Версия:</label>
        <a-select
          v-model:value="selectedVersion"
          :style="{width: '100%'}">
          <a-select-option
            v-for="(option) in versionOptions"
            :key="option"
            :value="option">
            {{ option }}
          </a-select-option>
        </a-select>
      </a-col>
      <a-col
        v-if="isFull && showVariantsDropdown"
        :md="6"
        :xs="24">
        <label>Вариант:</label>
        <a-select
          v-model:value="selectedVariant"
          :style="{width: '100%'}">
          <a-select-option
            v-for="variant in variantList"
            :key="variant.id"
            :value="variant.plan_id">
            {{ variant.name }}
          </a-select-option>
        </a-select>
      </a-col>
    </a-row>

    <a-button
      :disabled="isButtonDisabled"
      :style="{ width: '100%', marginTop: '.5rem' }"
      @click="openWidget(widgetLinkConstructor)">
      Открыть собранную ссылку
    </a-button>
    <a-button
      v-if="isFull"
      :disabled="isButtonDisabled"
      :style="{ width: '100%', marginTop: '.5rem' }"
      @click="openModal">
      Встроить виджет
    </a-button>
    <a-modal
      v-model:open="isModalOpen"
      :destroy-on-close="true"
      :footer="false"
      width="1000px"
      title="Встраивание виджета">
      <a-row type="flex">
        <div :style="{ width: '600px', height: '400px', backgroundColor: '#dbdbdb' }">
          <iframe
            title="widget-iframe"
            width="100%"
            height="100%"
            :src="widgetLinkConstructor"
            frameborder="0" />
        </div>
        <div
          class="code-block"
          :style="{ paddingLeft: '1rem' }">
          <code>{{ link }}</code>
          <a-button
            :style="{ width: '100%' }"
            @click="copyIframeCode">
            Копировать
          </a-button>
        </div>
      </a-row>
    </a-modal>
  </a-card>

  <a-button
    v-if="unrealStatus === StepStatus.FINISH"
    :style="{ width: '100%', marginTop: '.5rem' }"
    @click="openWidget(defaultWidgetLink)">
    Открыть виджет
  </a-button>

  <a-button
    v-if="gltfStatus === StepStatus.FINISH"
    :style="{ width: '100%', marginTop: '.5rem' }"
    @click="openWidget(gltfWidgetLinkConstructor)">
    Открыть GLTF-виджет
  </a-button>

  <a-popconfirm
    v-if="!isWidgetCacheInvalidationInProgress"
    title="Сбросить кэш виджета?"
    ok-text="Да"
    cancel-text="Нет"
    @confirm="resetWidgetCache">
    <a-button
      :loading="isResetWidgetCacheLoading"
      :style="{ width: '100%', marginTop: '.5rem' }">
      Сбросить кэш виджета
    </a-button>
  </a-popconfirm>

  <div
    v-else
    class="widget-cache-progress-wrapper"
    :style="{ '--progress': widgetCacheInvalidationProgress / 100 }">
    <a-button
      disabled
      :style="{ width: '100%', marginTop: '.5rem' }">
      Сброс кэша виджета: {{ widgetCacheInvalidationProgress }}%
    </a-button>
  </div>
</template>

<script setup>
import { useStore } from 'vuex';
import { message, Modal } from 'ant-design-vue';
import {
  computed, ref, onBeforeUnmount, watch,
} from 'vue';
import { api } from '@/api';
import { usePageVisibility } from '@/composables/usePageVisibility';
import { getGltfWidgetRoute, getWidgetRoute } from '@/router/helpers';
import { roomTypes } from '@/enums/roomTypes';
import { availableLanguages } from '@/enums/languages';
import { fuzzyMatchOption } from '@/helpers/fuzzyMatchOption';
import { StepStatus } from '@/enums/StepStatus';

const props = defineProps({
  planId: {
    type: String,
    default: '',
  },
  isFull: {
    type: Boolean,
    default: true,
  },
  queryPlan: {
    type: String,
    default: '',
  },
  cacheScope: {
    type: String,
    default: 'service',
  },
  askCacheBeforeOpen: {
    type: Boolean,
    default: false,
  },
  // Если родитель уже загрузил планировку, отдаём invalidations пропом,
  // чтобы не тянуть весь план ещё раз ради одного поля.
  planInvalidations: {
    type: Array,
    default: null,
  },
});

const store = useStore();
const { isVisible } = usePageVisibility();

let brandingList;

if (props.isFull) {
  store.dispatch('substances/getMinimalBrandingsList');
  brandingList = computed(() => store.state.substances.minimalBrandingsList);
}

const unrealStatus = computed(() => (props.cacheScope === 'crm'
  ? store.state.crm.currentPlan.plan?.status?.unreal
  : store.state.plans.current.status.unreal));
const gltfStatus = computed(() => (props.cacheScope === 'crm'
  ? store.state.crm.currentPlan.plan?.status?.gltf
  : store.state.plans.current.status.gltf));

const primaryFov = ref(75);
const variantList = props.isFull ? computed(() => store.state.crm.currentPlan.widget_variants) : ref([]);
const showVariantsDropdown = variantList.value.length > 0;
const selectedVariant = ref(null);
const data = ref(null);
const miniPlanOptions = ['svg', 'original'];
const versionOptions = ['v4', 'v2'];

const showFloorDropdown = ref(false);
const selectedBranding = ref(null);
const selectedRoomType = ref(null);
const selectedMiniPlan = ref(miniPlanOptions[0]);
const selectedVersion = ref(versionOptions[0]);
const selectedFloor = props.isFull ? ref(0) : computed(() => store.state.plans.widgetData.currentFloor);
const selectedLanguage = ref('en');
const floorList = computed(() => store.state.plans.widgetData.floors);
const floorRoomOptions = ref([]);
const currentFloorRoomOptions = computed(() => floorRoomOptions.value[selectedFloor.value] || []);

const isButtonDisabled = computed(() => selectedVersion.value === 'v2'
  && (props.isFull ? !selectedBranding.value : true));

const setRooms = () => {
  if (!floorList.value) {
    console.error('floorList is empty');
    return;
  }
  if (floorList.value.length > 1) {
    showFloorDropdown.value = true;
  }
  floorRoomOptions.value = new Array(floorList.value.length);

  floorList.value.forEach((floor, i) => {
    floor.Units.forEach((unit) => {
      const floorOptions = [];
      unit.Rooms.forEach((room) => {
        const roomType = room.Type;
        const roomId = room.ID;
        const floorNumber = floor.Number;

        const cameraPoints = floor.CameraPoints.filter((point) => point.RoomID === roomId);

        if (cameraPoints.length > 0) {
          cameraPoints.forEach((cameraPoint) => {
            const cameraId = cameraPoint.ID;
            const roomOptions = {
              roomType,
              cameraId,
              floorNumber,
            };
            floorOptions.push(roomOptions);
          });
        }
      });

      floorRoomOptions.value[i] = floorOptions;
    });
  });
};

const getCameraNumber = (option) => {
  const roomsWithSameName = currentFloorRoomOptions.value.filter((room) => room.roomType === option.roomType);
  const index = roomsWithSameName.findIndex((room) => room.cameraId === option.cameraId);
  return index + 1;
};

const DEFAULT_FOV = 75;
const DEFAULT_MINIPLAN = 'svg';
const DEFAULT_LANGUAGE = 'en';
const DEFAULT_VERSION = 'v4';
const DEFAULT_FLOOR = 0;

const DEFAULT_WIDGET_BASE_URL = 'https://base.hart-estate.ru';

const buildWidgetUrl = (params, baseUrl) => getWidgetRoute(params)
  .toString()
  .replace(/^https?:\/\/[^/]+\/widget/, baseUrl);

// Быстрая ссылка на стандартный виджет: намеренно без брендинга и параметров конструктора,
// иначе кнопка молча наследует выбор из карточки и перестаёт быть «как у нас по умолчанию».
const defaultWidgetLink = computed(() => buildWidgetUrl(
  { [props.queryPlan]: props.planId },
  DEFAULT_WIDGET_BASE_URL,
));

const widgetLinkConstructor = computed(() => {
  const params = {
    [props.queryPlan]: props.planId,
  };

  if (primaryFov.value !== DEFAULT_FOV) params.primaryFov = primaryFov.value;
  if (selectedMiniPlan.value !== DEFAULT_MINIPLAN) params.miniplan = selectedMiniPlan.value;
  if (selectedLanguage.value !== DEFAULT_LANGUAGE) params.locale = selectedLanguage.value;
  if (selectedFloor.value !== DEFAULT_FLOOR) params.primaryFloorNumber = selectedFloor.value + 1;
  if (props.isFull && selectedVariant.value) params.primaryVariantId = selectedVariant.value;
  if (selectedRoomType.value) params.primaryCameraPointId = selectedRoomType.value;

  let baseUrl = '';
  if (selectedVersion.value === 'v2') {
    if (props.isFull && selectedBranding.value) {
      const selectedBrandingData = brandingList.value.find((branding) => branding.id === selectedBranding.value);
      if (!selectedBrandingData) return '';

      const domainPrefix = selectedBrandingData.domain_prefix;
      const { domain } = selectedBrandingData;
      baseUrl = `https://${domainPrefix}.${domain}`;
    } else {
      return '';
    }
  } else if (props.isFull && selectedBranding.value) {
    const selectedBrandingData = brandingList.value.find((branding) => branding.id === selectedBranding.value);
    if (!selectedBrandingData) return '';

    const domainPrefix = selectedBrandingData.domain_prefix;
    const { domain } = selectedBrandingData;
    baseUrl = `https://${domainPrefix}.${domain}/beta/`;
  } else {
    baseUrl = DEFAULT_WIDGET_BASE_URL;
  }

  return buildWidgetUrl(params, baseUrl);
});

const gltfWidgetLinkConstructor = computed(() => {
  const params = {
    [props.queryPlan]: props.planId,
  };
  return getGltfWidgetRoute(params).toString();
});

setRooms();

const link = computed(() => `<iframe src="${widgetLinkConstructor.value}"
  frameborder="0" width="100%" height="100%"/>`);

const getNoCacheUrl = (url) => {
  try {
    const parsedUrl = new URL(url);
    parsedUrl.searchParams.set('noCache', '1');
    return parsedUrl.toString();
  } catch (error) {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}noCache=1`;
  }
};

const openInNewTab = (url) => {
  window.open(url, '_blank', 'noopener,noreferrer');
};

const openWidget = (url) => {
  if (!url) {
    return;
  }

  if (!props.askCacheBeforeOpen) {
    openInNewTab(url);
    return;
  }

  Modal.confirm({
    title: 'Открыть с кэшем или без?',
    okText: 'С кэшем',
    cancelText: 'Без кэша',
    centered: true,
    onOk: () => {
      openInNewTab(url);
    },
    onCancel: () => {
      openInNewTab(getNoCacheUrl(url));
    },
  });
};

const copyIframeCode = () => {
  navigator.clipboard.writeText(link.value)
    .then(() => {
      message.success('Код iframe скопирован в буфер обмена');
    })
    .catch((err) => {
      console.error('Ошибка копирования:', err);
      message.error('Не удалось скопировать код iframe');
    });
};
const isModalOpen = ref(false);
const openModal = () => isModalOpen.value = true;

const invalidations = ref([]);
const invalidationsToDisplay = ref([]);
const isResetWidgetCacheLoading = ref(false);
const invalidationsPollingTimer = ref(null);
const completionHoldTimer = ref(null);
const isCompletionHoldPendingVisibility = ref(false);
const INVALIDATIONS_POLLING_INTERVAL = 10000;
const COMPLETION_VISIBILITY_HOLD_MS = 5000;

const INVALIDATION_STATUS_PROGRESS = {
  queued: 0,
  running: 50,
  completed: 100,
  failed: 100,
};

const isWidgetCacheInvalidationInProgress = computed(() => invalidationsToDisplay.value.length > 0);
const hasActiveInvalidations = computed(() => invalidations.value
  .some(({ status }) => [StepStatus.WAIT, StepStatus.PROCESS].includes(status)
    || ['queued', 'running'].includes(status)));
const hasDisplayedFinishedInvalidations = computed(() => invalidationsToDisplay.value.length > 0
  && invalidationsToDisplay.value.every((item) => Boolean(item.finished_at)));

const widgetCacheInvalidationProgress = computed(() => {
  if (!invalidationsToDisplay.value.length) {
    return 0;
  }

  const total = invalidationsToDisplay.value.reduce((sum, { status }) => {
    const normalizedStatus = (status || '').toLowerCase();
    return sum + (INVALIDATION_STATUS_PROGRESS[normalizedStatus] ?? 0);
  }, 0);

  return Math.round(total / invalidationsToDisplay.value.length);
});

const stopInvalidationsPolling = () => {
  if (invalidationsPollingTimer.value) {
    clearInterval(invalidationsPollingTimer.value);
    invalidationsPollingTimer.value = null;
  }
};

const clearCompletionHoldTimer = () => {
  if (completionHoldTimer.value) {
    clearTimeout(completionHoldTimer.value);
    completionHoldTimer.value = null;
  }
};

const finishCompletionState = () => {
  clearCompletionHoldTimer();
  isCompletionHoldPendingVisibility.value = false;
  invalidationsToDisplay.value = [];
};

const scheduleCompletionHide = () => {
  clearCompletionHoldTimer();

  if (!isVisible.value) {
    isCompletionHoldPendingVisibility.value = true;
    return;
  }

  isCompletionHoldPendingVisibility.value = false;
  completionHoldTimer.value = setTimeout(() => {
    finishCompletionState();
  }, COMPLETION_VISIBILITY_HOLD_MS);
};

const syncDisplayedInvalidations = (serverInvalidations) => {
  if (serverInvalidations.length > 0) {
    invalidationsToDisplay.value = serverInvalidations;

    if (serverInvalidations.every((item) => Boolean(item.finished_at))) {
      scheduleCompletionHide();
    } else {
      clearCompletionHoldTimer();
      isCompletionHoldPendingVisibility.value = false;
    }
    return;
  }

  if (!hasDisplayedFinishedInvalidations.value) {
    invalidationsToDisplay.value = [];
    return;
  }

  if (!completionHoldTimer.value && !isCompletionHoldPendingVisibility.value) {
    scheduleCompletionHide();
  }
};

const fetchInvalidations = async () => {
  if (!props.planId) {
    invalidations.value = [];
    invalidationsToDisplay.value = [];
    return;
  }

  try {
    let serverInvalidations = [];

    if (props.cacheScope === 'crm') {
      const { data: crmPlan3d } = await api.getCrmPlan3dById(props.planId);
      if (Array.isArray(crmPlan3d?.invalidations)) {
        serverInvalidations = crmPlan3d.invalidations;
      } else if (Array.isArray(crmPlan3d?.plan?.invalidations)) {
        serverInvalidations = crmPlan3d.plan.invalidations;
      }
    } else {
      const { data: plan } = await api.getServicePlanById(props.planId);
      serverInvalidations = Array.isArray(plan?.invalidations) ? plan.invalidations : [];
    }

    invalidations.value = serverInvalidations;
    syncDisplayedInvalidations(serverInvalidations);
  } catch (error) {
    console.error('Ошибка загрузки invalidations:', error);
  }
};

const startInvalidationsPolling = () => {
  if (invalidationsPollingTimer.value) {
    return;
  }

  invalidationsPollingTimer.value = setInterval(async () => {
    await fetchInvalidations();
  }, INVALIDATIONS_POLLING_INTERVAL);
};

const resetWidgetCache = async () => {
  try {
    isResetWidgetCacheLoading.value = true;
    if (props.cacheScope === 'crm') {
      await api.invalidateCrmWidgetCache(props.planId);
    } else {
      await api.invalidateWidgetCache(props.planId);
    }
    message.success('Сброс кэша виджета запущен');
    await fetchInvalidations();
  } catch (error) {
    console.error('Ошибка при сбросе кэша виджета:', error);
    message.error('Не удалось сбросить кэш виджета');
  } finally {
    isResetWidgetCacheLoading.value = false;
  }
};

const applyInvalidations = (serverInvalidations) => {
  invalidations.value = serverInvalidations;
  syncDisplayedInvalidations(serverInvalidations);
};

watch(
  () => [props.planId, props.planInvalidations],
  async ([, providedInvalidations]) => {
    if (Array.isArray(providedInvalidations)) {
      applyInvalidations(providedInvalidations);
      return;
    }

    await fetchInvalidations();
  },
  { immediate: true },
);

watch(
  () => hasActiveInvalidations.value,
  (hasActive) => {
    if (hasActive) {
      startInvalidationsPolling();
      return;
    }

    stopInvalidationsPolling();
  },
  { immediate: true },
);

watch(
  () => isVisible.value,
  (visible) => {
    if (visible && isCompletionHoldPendingVisibility.value) {
      scheduleCompletionHide();
    }
  },
);

onBeforeUnmount(() => {
  stopInvalidationsPolling();
  clearCompletionHoldTimer();
});
</script>

<style scoped>
.code-block {
  display: flex;
  flex-direction: column;
  width: calc(100% - 600px);
}
.code-block code {
  margin-bottom: auto;
}

.widget-cache-progress-wrapper {
  margin-top: .5rem;
  padding: 2px;
  border-radius: 8px;
  background: conic-gradient(
    #52c41a calc(var(--progress) * 1turn),
    #d9d9d9 0turn
  );
}

.widget-cache-progress-wrapper :deep(.ant-btn) {
  margin-top: 0 !important;
}
</style>
